import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { DashboardSortOrderOption } from '../../../model/dashboard-sort-order.model';
import { Bucket } from '../../../model/dashoard.model';
import { Job } from '../../../model/job.model';
import { UserService } from '../../../services/user.service';
import { BucketSortName } from '../../../types/dashboardSortOrder.type';
import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { RepairerTileComponent } from '../repairer-tile/repairer-tile.component';
import { MatDivider } from '@angular/material/divider';
import { OrderByEpochTimePipe } from '../../../pipes/orderByEpochTime.pipe';
import {
  BUCKET_FILTER_NO_RESULTS_MESSAGE,
  BUCKET_FILTER_PLACEHOLDER,
  BUCKET_SORT_OPTIONS
} from './repairer-buckets.const';

@Component({
  selector: 'app-repairer-buckets',
  templateUrl: './repairer-buckets.component.html',
  styleUrls: ['./repairer-buckets.component.scss'],
  imports: [
    NgTemplateOutlet,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    BrowserAutoFillDisabledDirective,
    FormsModule,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatLabel,
    MatSelect,
    MatOption,
    RepairerTileComponent,
    MatDivider,
    TitleCasePipe,
    OrderByEpochTimePipe
  ]
})
export class RepairerBucketsComponent implements OnInit {
  bucket = input.required<Bucket>();
  getJobs = output<void>();

  #userService = inject(UserService);

  allowFilter = false;
  allowSort = false;
  sortingBucketName: BucketSortName;
  filterPlaceholder = BUCKET_FILTER_PLACEHOLDER;
  noResultFilterMessage = BUCKET_FILTER_NO_RESULTS_MESSAGE;
  sortOptionsList = [...BUCKET_SORT_OPTIONS];
  sortOption: DashboardSortOrderOption = this.sortOptionsList[0]; // "Date received (oldest to newest)"

  filterValue = signal('');

  searchedJobs = computed(() => {
    const searchValue = this.filterValue().toLowerCase();
    return searchValue
      ? (this.bucket()?.jobBuckets?.filter((job) => {
          const insuranceCompany = job.insco?.toLowerCase();
          const assessingCompany = job.assessingCompany?.toLowerCase();
          return insuranceCompany?.includes(searchValue) || assessingCompany?.includes(searchValue);
        }) ?? [])
      : (this.bucket()?.jobBuckets ?? []);
  });

  hasNoMatchingJobs = computed(() => this.filterValue() && !this.searchedJobs().length);

  filteredJobs = computed((): Job[] =>
    this.hasNoMatchingJobs() ? (this.bucket()?.jobBuckets ?? []) : this.searchedJobs()
  );

  badgedBucket = computed(() =>
    [
      'REQUEST_FOR_QUOTE',
      'MORE_INFO_REQUIRED',
      'MORE_INFO_SUPPLEMENT',
      'READY_TO_INVOICE',
      'INVOICE_REJECTED'
    ].includes(this.bucket()?.bucketName)
  );

  ngOnInit(): void {
    this.#getFilterAndSortOption();
  }

  #getFilterAndSortOption(): void {
    const dashboardSortOrder = this.#userService.dashboardSortOrder();
    switch (this.bucket()?.id) {
      case 'requestForQuote':
        this.allowFilter = true;
        this.allowSort = true;
        this.sortingBucketName = 'requestForQuoteSortOrder';
        this.sortOption = this.sortOptionsList.find(({ name }) => name === dashboardSortOrder.requestForQuoteSortOrder);
        break;
      case 'pendingAssessment':
        this.allowSort = true;
        this.sortingBucketName = 'pendingAssessmentSortOrder';
        this.sortOption = this.sortOptionsList.find(
          ({ name }) => name === dashboardSortOrder.pendingAssessmentSortOrder
        );
        break;
      case 'authorised':
        this.allowSort = true;
        this.sortOptionsList = this.sortOptionsList.filter(({ value }) => value === 'lastReceivedDateTime');
        this.sortingBucketName = 'authorisedSortOrder';
        this.sortOption = this.sortOptionsList.find((option) => option.name === dashboardSortOrder.authorisedSortOrder);
        break;
      case 'selfAuthorised':
        this.allowSort = true;
        this.sortOptionsList = this.sortOptionsList.filter(({ value }) => value === 'lastReceivedDateTime');
        this.sortingBucketName = 'selfAuthorisedSortOrder';
        this.sortOption = this.sortOptionsList.find(({ name }) => name === dashboardSortOrder.selfAuthorisedSortOrder);
        break;
    }
  }

  optionComparisonFunction(option, value): boolean {
    return option.name === value.name;
  }

  onChangeDashboardSortOrder(event: MatSelectChange): void {
    if (event?.value && this.sortingBucketName) {
      this.#userService.updateDashboardOrder(this.sortingBucketName, event.value.name).subscribe();
    }
  }
}
